import React, { useEffect, useState } from "react";
import BarekamInput from "../../../../components/input";
import { Recipient } from "../../../../shared/models/user/recipient/recipient";
import Message from "../../../../components/message";
import { MessageSections } from "../../../../components/message/message";
import BarekamPhoneInput from "../../../../auth/phone-input/phone-input";
import { AxiosError } from "axios";
import { useAppSelector } from "../../../../../redux/store";
import { BarekamSelect } from "../../../../components/select/select";
import {
  BarekamMenuItem,
  SelectedBarekamMenuItem,
} from "../../../../components/select/styled/orders";
import { MessageLoader } from "../../../../../router/blocks/route-components/message.loader";
import moment, { Moment } from "moment";
import { DialogContent, Grid, PaperProps } from "@mui/material";
import {
  mapValidationErrors,
  saveRecipient,
} from "../../personal-info/personal-info.service";
import { SaveResultType } from "../../../../shared/enum/save-result-type";
import {
  StandardDialog,
  StandardDialogFooter,
} from "../../../../components/dialogs/standard-dialog/standard-dialog";
import { BarekamAttachment } from "../../../../components/attachment/attachment";
import BarekamDateInput from "../../../../components/input/barekam-date-input";
import { EditAddress } from "../../user-addresses/edit-address/edit-address";
import { UserAddress } from "../../../../shared/models/user/user-address";
import { RecipientAddress } from "../recipient-address/recipient-address";
import { getMessage } from "../../../../shared/helper-functions";

export const EditRecipient = ({
  recipient,
  recipientSaved,
  popupClosed,
  isReadonly,
}: EditRecipientProps) => {
  // TODO: most of the logic is moved to personal info service
  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const { recipientDocumentTypes } = useAppSelector(
    (state) => state.staticDataReducer,
  );
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  const [newRecipient, setNewRecipient] = useState<Recipient>({ ...recipient });
  const [fileInfo, setFileInfo] = useState<File>();
  const [addressToEdit, setAddressToEdit] = useState<UserAddress | undefined>();

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (newRecipient.document) {
      setFileInfo(newRecipient.document as File);
    }
  }, [newRecipient.document]);

  const handleChange = (
    value: any,
    fieldName:
      | "id"
      | "type"
      | "firstName"
      | "lastName"
      | "patronymic"
      | "phoneNumber"
      | "passportOrIdSerialNumber"
      | "documentExpirationDate"
      | "address"
      | "document"
      | "documentType"
      | "email",
  ) => {
    setErrors((prevState) => {
      delete prevState[fieldName];
      return prevState;
    });
    const modifiedRecipient = { ...newRecipient, [fieldName]: value };

    if (fieldName === "documentType") {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      modifiedRecipient.documentType = recipientDocumentTypes.find(
        (documentType) => documentType.recipientDocumentTypeId === value,
      );
    }

    if (fieldName === "passportOrIdSerialNumber" && modifiedRecipient?.documentType?.recipientDocumentTypeId === 1) {
      const pattern = /^[A-Z]{2}\d{7}$/;
      if(!pattern.test(value)) {
        setErrors({
          passportOrIdSerialNumber: 'format_error'
        })
      } else {
        setErrors((prevState) => ({
          ...prevState,
          passportOrIdSerialNumber: '',
        }))
      }
    }

    if (fieldName === "passportOrIdSerialNumber" && modifiedRecipient?.documentType?.recipientDocumentTypeId === 2) {
      const pattern = /^\d{9}$/;
      if(!pattern.test(value)) {
        setErrors({
          passportOrIdSerialNumber: 'format_error'
        })
      } else {
        setErrors((prevState) => ({
          ...prevState,
          passportOrIdSerialNumber: '',
        }))
      }
    }

    setNewRecipient(modifiedRecipient);
  };

  const handleFileSelect = async (file: File | undefined) => {
    setFileInfo(file);

    handleChange(file || "", "document");
  };

  const handleEditAddressClose = (
    withChange: boolean,
    address?: UserAddress,
  ) => {
    if (!withChange) setAddressToEdit(undefined);

    if (withChange && address) {
      setNewRecipient((prevState) => {
        return {
          ...prevState,
          country: address.country,
          province: address.province,
          city: address.city,
          region: address.region,
          street: address.street,
          building: address.building,
          entrance: address.entrance,
          floor: address.floor,
          apartment: address.apartment,
          zipCode: address.zipCode,
        };
      });
      setAddressToEdit(undefined);
    }
  };

  const addRecipient = async () => {
    saveRecipient(newRecipient, recipient, true)
      .then((response) => {
        if (response.status === SaveResultType.SUCCESS) {
          recipientSaved &&
            recipientSaved({
              ...newRecipient,
              recipientId: response.payload.id,
            });

        } else if (response.payload instanceof AxiosError) {
          setErrors(mapValidationErrors(response.payload));

        } else if (response.status === SaveResultType.ERROR) {
          setErrors(mapValidationErrors(response.payload));
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError) {
          setErrors(mapValidationErrors(e));
        }
      });
  };

  const editRecipient = async () => {
    saveRecipient(newRecipient, recipient, false)
      .then((response) => {
        if (response.status === SaveResultType.SUCCESS) {
          recipientSaved && recipientSaved(newRecipient);
        } else if (response.payload instanceof AxiosError) {
          setErrors(mapValidationErrors(response.payload));
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError) {
          setErrors(mapValidationErrors(e));
        }
      });
  };

  return (
    <MessageLoader sectionName={MessageSections.USER}>
      <DialogContent>
        <Grid container columnSpacing={"1.5rem"} rowSpacing={"1.5rem"}>
          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "recipient_first_name",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newRecipient.firstName}
              onChange={(event) =>
                handleChange(event.target.value, "firstName")
              }
              width={"100%"}
              isReadonly={!!isReadonly}
              error={!!errors["firstName"]}
              errorMessage={
                errors["firstName"] &&
                getMessage(
                  errors["firstName"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              required
              validatePattern
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "recipient_last_name",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newRecipient.lastName}
              onChange={(event) => handleChange(event.target.value, "lastName")}
              width={"100%"}
              isReadonly={!!isReadonly}
              error={!!errors["lastName"]}
              errorMessage={
                errors["lastName"] &&
                getMessage(
                  errors["lastName"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              required
              validatePattern
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              key="recipient-email"
              customInputVariant={"standard"}
              placeholder={getMessage(
                "email",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              width={"100%"}
              isReadonly={!!isReadonly}
              value={newRecipient?.email}
              onChange={(event) => handleChange(event.target.value, "email")}
              error={!!errors["email"]}
              errorMessage={
                errors["email"] &&
                getMessage(
                  errors["email"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              required
              validatePattern
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamPhoneInput
              value={newRecipient.phoneNumber}
              onChange={(value: string) => handleChange(value, "phoneNumber")}
              isReadonly={!!isReadonly}
              placeholder={getMessage(
                "recipient_phone_number",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              error={!!errors["phoneNumber"]}
              errorMessage={
                errors["phoneNumber"] &&
                getMessage(
                  errors["phoneNumber"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
            />
          </Grid>

          <Grid item xs={12}>
            <BarekamSelect
              variant={"outlined"}
              defaultValue={""}
              label={getMessage(
                "document_type",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newRecipient.documentType?.recipientDocumentTypeId || ""}
              onChange={(event) =>
                handleChange(event.target.value, "documentType")
              }
              type={"standard"}
              disabled={isReadonly}
              required
              error={!!errors["documentType"]}
              errorMessage={
                errors["documentType"] &&
                getMessage(
                  errors["documentType"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
            >
              {recipientDocumentTypes
                .filter((type) => type.name !== "tid")
                .map((type) => {
                  if (
                    type.recipientDocumentTypeId ===
                    newRecipient.documentType?.recipientDocumentTypeId
                  ) {
                    return (
                      <SelectedBarekamMenuItem
                        value={type.recipientDocumentTypeId}
                        key={type.name}
                      >
                        <Message
                          messageKey={type.name}
                          sectionId={MessageSections.USER}
                        />
                      </SelectedBarekamMenuItem>
                    );
                  } else {
                    return (
                      <BarekamMenuItem
                        value={type.recipientDocumentTypeId}
                        key={type.name}
                      >
                        <Message
                          messageKey={type.name}
                          sectionId={MessageSections.USER}
                        />
                      </BarekamMenuItem>
                    );
                  }
                })}
            </BarekamSelect>
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamInput
              customInputVariant={"standard"}
              placeholder={getMessage(
                "passport_id",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              value={newRecipient.passportOrIdSerialNumber}
              onChange={(event) =>
                handleChange(event.target.value, "passportOrIdSerialNumber")
              }
              width={"100%"}
              isReadonly={!!isReadonly}
              error={!!errors["passportOrIdSerialNumber"]}
              errorMessage={
                errors["passportOrIdSerialNumber"] &&
                getMessage(
                  errors["passportOrIdSerialNumber"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
            />
          </Grid>

          <Grid item xs={matchesPhone ? 12 : 6}>
            <BarekamDateInput
              views={["year", "month", "day"]}
              value={
                newRecipient.documentExpirationDate
                  ? moment(newRecipient.documentExpirationDate)
                  : null
              }
              onChange={(event: Moment) =>
                handleChange(
                  event && event.format("YYYY-MM-DD"),
                  "documentExpirationDate",
                )
              }
              label={getMessage(
                "passport_expire_date",
                MessageSections.USER,
                messageCache,
                languageId,
              )}
              errorMessage={
                errors["documentExpirationDate"] &&
                getMessage(
                  errors["documentExpirationDate"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
              readOnly={!!isReadonly}
              required
              disablePast
            />
          </Grid>

          <Grid item xs={12}>
            <RecipientAddress
              recipient={newRecipient}
              setAddressToEdit={(address) => setAddressToEdit(address)}
            />
          </Grid>

          <Grid item xs={12}>
            <BarekamAttachment
              titleLabel={
                <Message
                  messageKey={"passport_photo"}
                  sectionId={MessageSections.USER}
                />
              }
              messageSection={MessageSections.USER}
              value={fileInfo}
              isSingle={true}
              isReadonly={!!isReadonly}
              onValueChange={(data: File) => handleFileSelect(data)}
              errorMessage={
                errors["document"] &&
                getMessage(
                  errors["document"],
                  MessageSections.USER,
                  messageCache,
                  languageId,
                )
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <StandardDialogFooter
        saveLabel={
          isReadonly ? undefined : (
            <Message
              messageKey={recipient.recipientId ? "save" : "add_recipient"}
              sectionId={MessageSections.USER}
            />
          )
        }
        onSave={() =>
          (addressToEdit || newRecipient?.building) ? recipient?.recipientId ? editRecipient() : addRecipient() : setAddressToEdit({} as UserAddress)
        }
        onCancel={() => popupClosed && popupClosed()}
      />

      <StandardDialog
        headerLabel={
          <Message
            messageKey={"edit_address"}
            sectionId={MessageSections.USER}
          />
        }
        open={!!addressToEdit}
      >
        <EditAddress
          address={addressToEdit || ({} as UserAddress)}
          onSave={handleEditAddressClose}
          isRecipientAddress
        />
      </StandardDialog>
    </MessageLoader>
  );
};

interface EditRecipientProps extends PaperProps {
  recipient: Recipient;
  recipientSaved?: (recipient: Recipient) => void;
  isReadonly?: boolean;
  popupClosed?: () => void;
}
